import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type LegalKey = 'privacy' | 'terms' | null;
const LEGAL_CONTENT: Record<Exclude<LegalKey, null>, {
  title: string;
  sections: {
    heading: string;
    body: string;
  }[];
}> = {
  privacy: {
    title: 'Política de Privacidad',
    sections: [{
      heading: '1. Información que Recopilamos',
      body: 'Recopilamos información personal que tú nos proporcionas voluntariamente al realizar una compra, incluyendo tu nombre, dirección de correo electrónico y datos de pago. Los pagos son procesados por proveedores externos seguros y no almacenamos información de tarjetas de crédito en nuestros servidores.'
    }, {
      heading: '2. Uso de la Información',
      body: 'Utilizamos tu información para procesar pedidos, enviarte el producto digital adquirido, comunicarte actualizaciones relevantes sobre el contenido, y mejorar tu experiencia. Nunca venderemos ni compartiremos tu información personal con terceros con fines comerciales.'
    }, {
      heading: '3. Cookies y Seguimiento',
      body: 'Nuestro sitio web utiliza cookies para mejorar la experiencia del usuario y analizar el tráfico. Puedes configurar tu navegador para rechazar cookies, aunque esto puede limitar algunas funciones del sitio.'
    }, {
      heading: '4. Almacenamiento de Datos',
      body: 'Tus datos se almacenan de manera segura en servidores protegidos con cifrado de nivel industrial. Conservamos tu información únicamente durante el tiempo necesario para cumplir con los fines descritos en esta política.'
    }, {
      heading: '5. Tus Derechos',
      body: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. También puedes solicitar la portabilidad de tus datos o limitar su procesamiento.'
    }, {
      heading: '6. Cambios a esta Política',
      body: 'Podemos actualizar esta política de privacidad ocasionalmente. Cualquier cambio será publicado en esta página con la fecha de actualización correspondiente.'
    }]
  },
  terms: {
    title: 'Términos y Condiciones',
    sections: [{
      heading: '1. Aceptación de los Términos',
      body: 'Al acceder y realizar una compra en este sitio web, aceptas cumplir con estos términos y condiciones. Si no estás de acuerdo con alguno de los términos, por favor no utilices nuestros servicios.'
    }, {
      heading: '2. Producto Digital',
      body: 'El Manual "Master en Repostería en Casa" es un producto 100% digital que se entrega en formato PDF. Tras realizar el pago, recibirás un enlace de descarga inmediata. El producto es para uso personal y no puede ser redistribuido, revendido o compartido sin autorización por escrito.'
    }, {
      heading: '3. Propiedad Intelectual',
      body: 'Todo el contenido del manual, incluyendo recetas, imágenes, técnicas y métodos, está protegido por derechos de autor. La reproducción total o parcial sin consentimiento expreso está prohibida y constituye una violación a las leyes de propiedad intelectual.'
    }, {
      heading: '4. Política de Reembolsos',
      body: 'Debido a la naturaleza digital del producto y la entrega inmediata del contenido, las ventas son finales. Sin embargo, ofrecemos una garantía de satisfacción de 30 días: si por alguna razón el contenido no cumple con tus expectativas, contáctanos dentro de ese período para evaluar tu caso.'
    }, {
      heading: '5. Limitación de Responsabilidad',
      body: 'Los resultados al aplicar las técnicas del manual pueden variar según la práctica y dedicación del usuario. No garantizamos ingresos específicos derivados del uso del contenido como negocio. Cualquier emprendimiento implica riesgos que son responsabilidad del usuario.'
    }, {
      heading: '6. Modificaciones',
      body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en este sitio.'
    }, {
      heading: '7. Legislación Aplicable',
      body: 'Estos términos se rigen por las leyes aplicables al país desde el cual se opera el servicio. Cualquier disputa será resuelta a través de los canales legales correspondientes.'
    }]
  }
};
export function Footer() {
  const [openModal, setOpenModal] = useState<LegalKey>(null);
  // Lock body scroll when modal is open
  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [openModal]);
  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenModal(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);
  return <>
      <footer className="bg-espresso text-cream-dark py-12 px-4 sm:px-6 lg:px-8 border-t border-white/10 text-center" data-id="element-80">
        <div className="max-w-7xl mx-auto" data-id="element-81">
          <h3 className="font-serif text-2xl font-bold text-white mb-4 italic" data-id="element-82">
            Milhojas & Galletas Gourmet
          </h3>
          <p className="text-sm text-cream-dark/80 mb-8 max-w-md mx-auto" data-id="element-83">
            Transformando la pasión por la repostería en negocios rentables
            alrededor del mundo.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-8" data-id="element-84">
            <button onClick={() => setOpenModal('privacy')} className="text-sm text-cream-dark/90 hover:text-caramel-light transition-colors underline-offset-4 hover:underline" data-id="element-85">
              Política de Privacidad
            </button>
            <span className="text-cream-dark/40" data-id="element-86">•</span>
            <button onClick={() => setOpenModal('terms')} className="text-sm text-cream-dark/90 hover:text-caramel-light transition-colors underline-offset-4 hover:underline" data-id="element-87">
              Términos y Condiciones
            </button>
          </div>

          <div className="text-xs text-cream-dark/60 space-y-2" data-id="element-88">
            <p data-id="element-89">
              Este sitio no es parte del sitio web de Facebook o Facebook Inc.
              Además, este sitio NO está respaldado por Facebook de ninguna
              manera.
            </p>
            <p data-id="element-90">
              &copy; {new Date().getFullYear()} Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Legal Modal */}
      <AnimatePresence data-id="element-91">
        {openModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" onClick={() => setOpenModal(null)} role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-92">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-espresso/80 backdrop-blur-sm" data-id="element-93" />

            {/* Modal panel */}
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative bg-cream rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col border border-caramel/20" data-id="element-94">
              {/* Header */}
              <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-caramel/10 shrink-0" data-id="element-95">
                <h2 id="legal-modal-title" className="text-2xl sm:text-3xl font-serif font-bold text-espresso" data-id="element-96">
                  {LEGAL_CONTENT[openModal].title}
                </h2>
                <button onClick={() => setOpenModal(null)} aria-label="Cerrar" className="w-10 h-10 rounded-full bg-espresso/5 hover:bg-espresso/10 flex items-center justify-center text-espresso transition-colors shrink-0" data-id="element-97">
                  <X className="w-5 h-5" data-id="element-98" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="overflow-y-auto px-6 sm:px-8 py-6 space-y-6" data-id="element-99">
                {LEGAL_CONTENT[openModal].sections.map((section, i) => <div key={i} data-id="element-100">
                    <h3 className="font-serif text-lg font-bold text-espresso mb-2" data-id="element-101">
                      {section.heading}
                    </h3>
                    <p className="text-espresso-light font-sans leading-relaxed text-[15px]" data-id="element-102">
                      {section.body}
                    </p>
                  </div>)}

                <div className="pt-4 mt-4 border-t border-caramel/10" data-id="element-103">
                  <p className="text-xs text-espresso-light/70 italic" data-id="element-104">
                    Última actualización:{' '}
                    {new Date().toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 sm:px-8 py-4 border-t border-caramel/10 shrink-0 flex justify-end" data-id="element-105">
                <button onClick={() => setOpenModal(null)} className="px-6 py-2.5 bg-caramel text-white font-medium rounded-xl hover:bg-caramel-dark transition-colors" data-id="element-106">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}